import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft, Sparkles, Clock, CheckCircle, Users, TrendingUp, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import LightBackground from '@/components/LightBackground';
import { motion } from 'framer-motion';

interface AffiliateReward {
  id: string;
  referrer_id: string;
  referred_id: string;
  amount: number;
  status: string;
  reward_type: string;
  payment_date: string | null;
  period_start: string | null;
  period_end: string | null;
  created_at: string;
  referrer?: { name: string; email: string };
  referred?: { name: string; email: string; plan: string };
}

interface AffiliateStats {
  totalReferrers: number;
  totalReferred: number;
  totalPaid: number;
  totalPending: number;
}

const AdminAffiliates = () => {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [rewards, setRewards] = useState<AffiliateReward[]>([]);
  const [stats, setStats] = useState<AffiliateStats>({
    totalReferrers: 0,
    totalReferred: 0,
    totalPaid: 0,
    totalPending: 0
  });
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState<string | null>(null);

  useEffect(() => {
    if (!authLoading && (!user || !isAdmin)) {
      navigate('/login');
    }
  }, [user, isAdmin, authLoading, navigate]);

  useEffect(() => {
    if (user && isAdmin) {
      fetchRewards();
      fetchStats();
    }
  }, [user, isAdmin]);

  const fetchRewards = async () => {
    try {
      const { data, error } = await supabase
        .from('affiliate_rewards')
        .select(`
          *,
          referrer:clients!affiliate_rewards_referrer_id_fkey(name, email),
          referred:clients!affiliate_rewards_referred_id_fkey(name, email, plan)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setRewards(data || []);
    } catch (error) {
      console.error('Error fetching rewards:', error);
      toast({
        title: 'Error',
        description: 'Failed to load affiliate rewards',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      // Get unique referrers
      const { data: referrers } = await supabase
        .from('clients')
        .select('id')
        .not('referred_by', 'is', null);
      
      const uniqueReferrers = new Set(referrers?.map(r => r.id) || []);

      // Get total referred count
      const { count: referredCount } = await supabase
        .from('clients')
        .select('*', { count: 'exact', head: true })
        .not('referred_by', 'is', null);

      // Get paid rewards total
      const { data: paidData } = await supabase
        .from('affiliate_rewards')
        .select('amount')
        .eq('status', 'paid');
      
      const totalPaid = paidData?.reduce((sum, r) => sum + Number(r.amount), 0) || 0;

      // Get pending rewards total
      const { data: pendingData } = await supabase
        .from('affiliate_rewards')
        .select('amount')
        .eq('status', 'pending');
      
      const totalPending = pendingData?.reduce((sum, r) => sum + Number(r.amount), 0) || 0;

      setStats({
        totalReferrers: uniqueReferrers.size,
        totalReferred: referredCount || 0,
        totalPaid,
        totalPending
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const markAsPaid = async (rewardId: string) => {
    setProcessingId(rewardId);
    try {
      // Find the reward to determine credit amount and referrer
      const reward = rewards.find(r => r.id === rewardId);
      if (!reward) throw new Error('Reward not found');

      // Determine credits: 100 for monthly referral, 500 for lifetime
      const referredPlan = reward.referred?.plan?.toLowerCase() || '';
      const creditsToGrant = referredPlan.includes('lifetime') ? 500 : 100;

      // Mark reward as paid
      const { error } = await supabase
        .from('affiliate_rewards')
        .update({ 
          status: 'paid',
          payment_date: new Date().toISOString(),
          amount: creditsToGrant
        })
        .eq('id', rewardId);

      if (error) throw error;

      // Grant credits to the referrer
      const { error: creditError } = await supabase
        .from('ai_credits')
        .update({ balance: supabase.rpc ? undefined : undefined })
        .eq('client_id', reward.referrer_id);

      // Use raw update to increment balance
      const { error: incrementError } = await supabase.rpc('consume_ai_credits', {
        p_client_id: reward.referrer_id,
        p_tool_type: `referral_reward_${referredPlan.includes('lifetime') ? 'lifetime' : 'monthly'}`,
        p_credits: -creditsToGrant // negative to ADD credits
      });

      // If RPC fails (e.g., no record), create/upsert directly
      if (incrementError) {
        console.warn('RPC credit grant failed, trying direct upsert:', incrementError);
        await supabase
          .from('ai_credits')
          .upsert({
            client_id: reward.referrer_id,
            balance: creditsToGrant,
            monthly_allowance: 0,
          }, { onConflict: 'client_id' });
        
        // Also update existing balance by adding credits
        const { data: existing } = await supabase
          .from('ai_credits')
          .select('balance')
          .eq('client_id', reward.referrer_id)
          .single();
        
        if (existing) {
          await supabase
            .from('ai_credits')
            .update({ balance: existing.balance + creditsToGrant })
            .eq('client_id', reward.referrer_id);
        }
      }

      // Log the credit gift
      await supabase.from('ai_usage_log').insert({
        client_id: reward.referrer_id,
        tool_type: `referral_reward_${referredPlan.includes('lifetime') ? 'lifetime' : 'monthly'}`,
        credits_used: -creditsToGrant,
      });

      toast({
        title: 'Credits Granted! ✨',
        description: `${creditsToGrant} credits added to ${reward.referrer?.name || 'referrer'}'s account`
      });

      fetchRewards();
      fetchStats();
    } catch (error) {
      console.error('Error updating reward:', error);
      toast({
        title: 'Error',
        description: 'Failed to update reward status',
        variant: 'destructive'
      });
    } finally {
      setProcessingId(null);
    }
  };

  const cancelReward = async (rewardId: string) => {
    setProcessingId(rewardId);
    try {
      const { error } = await supabase
        .from('affiliate_rewards')
        .update({ status: 'cancelled' })
        .eq('id', rewardId);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Reward cancelled'
      });

      fetchRewards();
      fetchStats();
    } catch (error) {
      console.error('Error cancelling reward:', error);
      toast({
        title: 'Error',
        description: 'Failed to cancel reward',
        variant: 'destructive'
      });
    } finally {
      setProcessingId(null);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'paid':
        return <Badge className="bg-green-100 text-green-700 border-green-200">Paid</Badge>;
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-700 border-amber-200">Pending</Badge>;
      case 'cancelled':
        return <Badge className="bg-red-100 text-red-700 border-red-200">Cancelled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const filterRewardsByStatus = (status: string) => {
    if (status === 'all') return rewards;
    return rewards.filter(r => r.status === status);
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
      <LightBackground variant="subtle" />
      
      <div className="container mx-auto px-4 py-8 relative z-10">
        {/* Header */}
        <motion.div 
          className="flex items-center gap-4 mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/admin')}
            className="text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              Affiliate <span className="text-primary">Management</span>
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              View and manage all affiliate rewards
            </p>
          </div>
        </motion.div>

        {/* Stats Cards */}
        <motion.div 
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <Card className="bg-white border-border shadow-sm rounded-2xl">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Referrers</p>
                  <p className="text-2xl font-semibold text-foreground">{stats.totalReferrers}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-border shadow-sm rounded-2xl">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Referred</p>
                  <p className="text-2xl font-semibold text-foreground">{stats.totalReferred}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-border shadow-sm rounded-2xl">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Credits Granted</p>
                  <p className="text-2xl font-semibold text-foreground">{stats.totalPaid.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-border shadow-sm rounded-2xl">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Pending</p>
                  <p className="text-2xl font-semibold text-foreground">{stats.totalPending.toLocaleString()} credits</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Rewards Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Card className="bg-white border-border shadow-sm rounded-2xl">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">
                Affiliate Rewards
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="all" className="w-full">
                <TabsList className="mb-4 bg-slate-100">
                  <TabsTrigger value="all">All ({rewards.length})</TabsTrigger>
                  <TabsTrigger value="pending">
                    Pending ({rewards.filter(r => r.status === 'pending').length})
                  </TabsTrigger>
                  <TabsTrigger value="paid">
                    Paid ({rewards.filter(r => r.status === 'paid').length})
                  </TabsTrigger>
                  <TabsTrigger value="cancelled">
                    Cancelled ({rewards.filter(r => r.status === 'cancelled').length})
                  </TabsTrigger>
                </TabsList>

                {['all', 'pending', 'paid', 'cancelled'].map(status => (
                  <TabsContent key={status} value={status}>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-border">
                            <TableHead className="text-muted-foreground">Referrer</TableHead>
                            <TableHead className="text-muted-foreground">Referred Member</TableHead>
                            <TableHead className="text-muted-foreground">Type</TableHead>
                            <TableHead className="text-muted-foreground">Credits</TableHead>
                            <TableHead className="text-muted-foreground">Status</TableHead>
                            <TableHead className="text-muted-foreground">Created</TableHead>
                            <TableHead className="text-muted-foreground">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filterRewardsByStatus(status).length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                                No rewards found
                              </TableCell>
                            </TableRow>
                          ) : (
                            filterRewardsByStatus(status).map(reward => (
                              <TableRow key={reward.id} className="border-border">
                                <TableCell>
                                  <div>
                                    <p className="font-medium text-foreground">{reward.referrer?.name || 'Unknown'}</p>
                                    <p className="text-xs text-muted-foreground">{reward.referrer?.email}</p>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <div>
                                    <p className="font-medium text-foreground">{reward.referred?.name || 'Unknown'}</p>
                                    <p className="text-xs text-muted-foreground">{reward.referred?.plan}</p>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <Badge variant="outline" className="text-xs">
                                    {reward.reward_type}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  <span className="text-primary font-semibold">{Number(reward.amount).toLocaleString()} ✨</span>
                                </TableCell>
                                <TableCell>{getStatusBadge(reward.status)}</TableCell>
                                <TableCell className="text-muted-foreground text-sm">
                                  {format(new Date(reward.created_at), 'MMM d, yyyy')}
                                </TableCell>
                                <TableCell>
                                  {reward.status === 'pending' && (
                                    <div className="flex gap-2">
                                      <Button
                                        size="sm"
                                        onClick={() => markAsPaid(reward.id)}
                                        disabled={processingId === reward.id}
                                        className="bg-green-600 hover:bg-green-700 text-white text-xs"
                                      >
                                        <Sparkles className="w-3 h-3 mr-1" />
                                        Grant Credits
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="destructive"
                                        onClick={() => cancelReward(reward.id)}
                                        disabled={processingId === reward.id}
                                        className="text-xs"
                                      >
                                        Cancel
                                      </Button>
                                    </div>
                                  )}
                                  {reward.status === 'paid' && reward.payment_date && (
                                    <span className="text-xs text-muted-foreground">
                                      Paid {format(new Date(reward.payment_date), 'MMM d, yyyy')}
                                    </span>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminAffiliates;
